import { useEffect, useRef, useState } from "react";
import { createChart, CandlestickSeries, LineStyle } from "lightweight-charts";
import { api } from "../lib/api";
import { useTheme } from "../context/ThemeContext";

const INTERVALS = [
  { v: "15", label: "15m" },
  { v: "60", label: "1H" },
  { v: "240", label: "4H" },
  { v: "D", label: "1D" },
];

export default function TradeChart({ trade }) {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const { theme } = useTheme();
  const [interval, setIntervalV] = useState("60");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let disposed = false;
    setLoading(true);
    setError(null);

    (async () => {
      let data = [];
      try {
        const res = await api.get("/market/klines", {
          params: { symbol: trade.symbol, interval, limit: 300 },
        });
        data = res.data;
      } catch (e) {
        if (!disposed) {
          setError("Market data unavailable for this symbol");
          setLoading(false);
        }
        return;
      }
      if (disposed || !containerRef.current) return;

      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
      }

      const css = getComputedStyle(document.documentElement);
      const cv = (name) => `hsl(${css.getPropertyValue(name).trim()})`;
      const colors = {
        bg: cv("--card"), text: cv("--muted-foreground"), grid: cv("--border") ,
        border: cv("--border"), up: cv("--profit"), down: cv("--loss"), entry: cv("--primary"),
      };

      const chart = createChart(containerRef.current, {
        height: 420,
        layout: {
          background: { color: colors.bg },
          textColor: colors.text,
          fontFamily: "'JetBrains Mono', monospace",
        },
        grid: {
          vertLines: { color: colors.grid },
          horzLines: { color: colors.grid },
        },
        timeScale: { timeVisible: true, borderColor: colors.border },
        rightPriceScale: { borderColor: colors.border },
        crosshair: { mode: 0 },
        localization: { locale: "en-US" },
      });
      chartRef.current = chart;

      const levels = [trade.entry_price, trade.stop_loss,
        ...(trade.take_profits || []).map((tp) => tp.target_price)]
        .filter((v) => v != null && !isNaN(v));

      const series = chart.addSeries(CandlestickSeries, {
        upColor: colors.up,
        downColor: colors.down,
        borderUpColor: colors.up,
        borderDownColor: colors.down,
        wickUpColor: colors.up,
        wickDownColor: colors.down,
        autoscaleInfoProvider: (original) => {
          const res = original();
          if (!res || !res.priceRange || !levels.length) return res;
          return {
            ...res,
            priceRange: {
              minValue: Math.min(res.priceRange.minValue, ...levels),
              maxValue: Math.max(res.priceRange.maxValue, ...levels),
            },
          };
        },
      });
      series.setData(data);

      series.createPriceLine({
        price: trade.entry_price,
        color: colors.entry,
        lineWidth: 2,
        lineStyle: LineStyle.Solid,
        title: `ENTRY ${trade.entry_price}`,
      });
      if (trade.stop_loss != null) {
        series.createPriceLine({
          price: trade.stop_loss,
          color: colors.down,
          lineWidth: 2,
          lineStyle: LineStyle.Dashed,
          title: `SL ${trade.stop_loss}`,
        });
      }
      (trade.take_profits || []).forEach((tp, i) => {
        if (tp.target_price != null) {
          series.createPriceLine({
            price: tp.target_price,
            color: colors.up,
            lineWidth: 1,
            lineStyle: LineStyle.Dashed,
            title: `TP${i + 1} ${tp.target_price}`,
          });
        }
      });
      chart.timeScale().fitContent();
      setLoading(false);

      const onResize = () => {
        if (containerRef.current) chart.applyOptions({ width: containerRef.current.clientWidth });
      };
      window.addEventListener("resize", onResize);
      chart._onResize = onResize;
    })();

    return () => {
      disposed = true;
      if (chartRef.current) {
        window.removeEventListener("resize", chartRef.current._onResize);
        chartRef.current.remove();
        chartRef.current = null;
      }
    };
  }, [trade, interval]);

  return (
    <div className="border border-border bg-card" data-testid="trade-chart">
      <div className="flex items-center justify-between px-4 py-2 border-b border-border">
        <span className="font-mono text-sm text-foreground">{trade.symbol}</span>
        <div className="flex gap-1">
          {INTERVALS.map((iv) => (
            <button
              key={iv.v}
              data-testid={`chart-interval-${iv.label}`}
              onClick={() => setIntervalV(iv.v)}
              className={`px-2 py-0.5 text-xs font-mono transition-colors duration-150 ${
                interval === iv.v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {iv.label}
            </button>
          ))}
        </div>
      </div>
      {error ? (
        <div className="h-[420px] flex items-center justify-center text-muted-foreground text-sm" data-testid="chart-error">
          {error}
        </div>
      ) : (
        <div ref={containerRef} className="w-full" style={{ minHeight: 420 }}>
          {loading && <div className="h-[420px] flex items-center justify-center text-muted-foreground text-sm">Loading chart…</div>}
        </div>
      )}
    </div>
  );
}
